/**
 * utils/idCardOcr.js — อ่านข้อความจากรูปบัตรประชาชน/พาสปอร์ตด้วย OCR (tesseract.js)
 *
 * ทำงานในเบราว์เซอร์ล้วน — รูปบัตรไม่เคยถูกส่งขึ้นเซิร์ฟเวอร์หรือออกไปที่ third-party
 * และไม่มีฟังก์ชันไหนในไฟล์นี้เก็บ reference ของรูปไว้หลังอ่านเสร็จ (canvas เป็น
 * ตัวแปร local ที่ถูกล้างทิ้งใน finally)
 *
 * หลักการ parse: "ไม่มั่นใจ = ไม่กรอก" — ทุกฟิลด์คืน "" เมื่อ validate ไม่ผ่าน
 * แอดมินแก้ช่องว่างง่ายกว่าไล่หาช่องที่ระบบกรอกผิด
 */
import { createWorker } from "tesseract.js";

/* ช่องที่โมดูลนี้กรอกให้ได้ — ใช้นับจำนวนช่องที่อ่านสำเร็จเพื่อแจ้งแอดมิน */
export const FILLABLE_FIELDS = ["idCard", "fullName", "birthDate", "gender", "address"];

const EMPTY_FIELDS = {
  idCard: "", fullName: "", birthDate: "", gender: "",
  address: "", nationality: "", country: "", docType: "",
};

/* ═══════════════ tesseract worker (สร้างครั้งเดียว ใช้ซ้ำทุกใบ) ═══════════════
   ครั้งแรกต้องโหลด wasm + ไฟล์ภาษา tha/eng จาก CDN (~12MB) แล้วเบราว์เซอร์
   แคชไว้ใน IndexedDB เอง ครั้งต่อไปจึงเร็วและใช้ได้แม้ออฟไลน์ */
let workerPromise   = null;
let progressHandler = null;

function getWorker() {
  if (!workerPromise) {
    workerPromise = createWorker(["tha", "eng"], 1, {
      logger: (m) => {
        if (m.status === "recognizing text" && progressHandler) {
          progressHandler(Math.round((m.progress || 0) * 100));
        }
      },
    }).catch((err) => {
      workerPromise = null;      // ล้มเหลว → ให้ครั้งหน้าลองสร้างใหม่ได้
      throw err;
    });
  }
  return workerPromise;
}

/* เรียกตอน unmount — คืน memory ของ worker */
export async function terminateOcrWorker() {
  if (!workerPromise) return;
  const pending = workerPromise;
  workerPromise = null;
  try {
    const worker = await pending;
    await worker.terminate();
  } catch {
    /* worker สร้างไม่สำเร็จอยู่แล้ว ไม่มีอะไรต้องคืน */
  }
}

/* ═══════════════ เตรียมรูปก่อนเข้า OCR ═══════════════ */
/* รูปถ่ายจากมือถือมักมีบัตรกินพื้นที่แค่ครึ่งเฟรม — ย่อลงเหลือ 2000 แล้วตัวหนังสือบนบัตร
   จะเหลือสูงไม่ถึง 20px ซึ่ง tesseract อ่านไม่ออก ต้องเผื่อความละเอียดไว้มากกว่านี้
   แลกกับ OCR ช้าขึ้นราวเท่าตัว (~2 วิ → ~4 วิ) ซึ่งยังรับได้ */
const MAX_DIM = 3000;
const MIN_DIM = 1200;   // เล็กเกินนี้ตัวอักษรแตก อ่านไม่ออก — ขยายขึ้นก่อน

async function loadImage(file) {
  // createImageBitmap เร็วกว่าและหมุนรูปตาม EXIF ให้ (รูปจากมือถือมักตะแคง)
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      /* เบราว์เซอร์ไม่รองรับ option นี้ → ตกไปใช้ <img> ข้างล่าง */
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("ไม่สามารถอ่านไฟล์รูปนี้ได้")); };
    img.src = url;
  });
}

/**
 * โหลดรูป → ย่อ/ขยายให้อยู่ในช่วงที่ OCR ทำงานได้ดี → grayscale + ยืด contrast
 * คืน canvas ให้ tesseract ใช้ต่อ
 */
export async function preprocessToCanvas(file) {
  const img = await loadImage(file);
  try {
    const w0 = img.width, h0 = img.height;
    if (!w0 || !h0) throw new Error("ไม่สามารถอ่านไฟล์รูปนี้ได้");

    const longSide = Math.max(w0, h0);
    let scale = 1;
    if (longSide > MAX_DIM)      scale = MAX_DIM / longSide;
    else if (longSide < MIN_DIM) scale = Math.min(2, MIN_DIM / longSide);

    const canvas  = document.createElement("canvas");
    canvas.width  = Math.round(w0 * scale);
    canvas.height = Math.round(h0 * scale);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    toGrayscaleWithContrast(imageData.data);
    ctx.putImageData(imageData, 0, 0);

    return canvas;
  } finally {
    if (typeof img.close === "function") img.close();  // คืน memory ของ bitmap ทันที
  }
}

/* grayscale แล้วยืด contrast ด้วย percentile 2/98 — ช่วยมากกับรูปถ่ายที่แสงไม่สม่ำเสมอ */
function toGrayscaleWithContrast(data) {
  const hist = new Uint32Array(256);
  for (let i = 0; i < data.length; i += 4) {
    const g = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) | 0;
    data[i] = data[i + 1] = data[i + 2] = g;
    hist[g]++;
  }

  const total = data.length / 4;
  const lowCut = total * 0.02, highCut = total * 0.98;
  let acc = 0, lo = 0, hi = 255;
  for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= lowCut)  { lo = v; break; } }
  acc = 0;
  for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= highCut) { hi = v; break; } }
  if (hi - lo < 16) return;  // ภาพแบนเกินไป ยืดแล้วยิ่งพัง

  const span = hi - lo;
  const lut = new Uint8Array(256);
  for (let v = 0; v < 256; v++) {
    lut[v] = Math.max(0, Math.min(255, Math.round(((v - lo) / span) * 255)));
  }
  for (let i = 0; i < data.length; i += 4) {
    const g = lut[data[i]];
    data[i] = data[i + 1] = data[i + 2] = g;
  }
}

/* ═══════════════ entry point ═══════════════ */
/**
 * อ่านรูปบัตร → คืน { text, fields }
 * รูปถูกใช้เฉพาะภายในฟังก์ชันนี้ ไม่ถูกเก็บไว้ที่ไหนทั้งสิ้น
 */
export async function runIdCardOcr(file, { onProgress } = {}) {
  let canvas = null;
  progressHandler = typeof onProgress === "function" ? onProgress : null;
  try {
    const worker = await getWorker();
    canvas = await preprocessToCanvas(file);
    const { data } = await worker.recognize(canvas);
    const text = data?.text || "";
    return { text, fields: parseIdCardText(text) };
  } finally {
    progressHandler = null;
    if (canvas) { canvas.width = 0; canvas.height = 0; }  // ปล่อย pixel buffer ทิ้ง
  }
}

/* ═══════════════ helper: ตัวเลข / เลขบัตร ═══════════════ */
const THAI_DIGITS = "๐๑๒๓๔๕๖๗๘๙";

export function normalizeDigits(s) {
  return String(s || "").replace(/[๐-๙]/g, (d) => String(THAI_DIGITS.indexOf(d)));
}

/** tesseract มักคาย สระอำ ออกมาเป็น นิคหิต+สระอา (ํ + า) แทนอักขระเดี่ยว ำ
    ซึ่งหน้าตาเหมือนกันแต่ regex ไม่ match — ต้องรวบให้เป็นตัวเดียวก่อนเสมอ */
export function normalizeThaiText(s) {
  return String(s || "").replace(/ํา/g, "ำ");
}

/** checksum mod 11 ของเลขบัตรประชาชนไทย — ใช้กรองผลที่ OCR อ่านเพี้ยน */
export function validateThaiId(value) {
  const d = String(value || "").replace(/\D/g, "");
  if (d.length !== 13) return false;
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(d[i]) * (13 - i);
  return ((11 - (sum % 11)) % 10) === Number(d[12]);
}

/* ตัวอักษรที่ OCR มักสับสนกับตัวเลข — ใช้ซ่อมเลขบัตรในรอบสองเท่านั้น */
const OCR_DIGIT_FIX = {
  O: "0", o: "0", D: "0", Q: "0",
  I: "1", l: "1", i: "1", "|": "1",
  Z: "2", z: "2", A: "4", S: "5", s: "5",
  G: "6", T: "7", B: "8", g: "9", q: "9",
};
const DIGITISH = "[0-9OoDQIli|ZzASsGTBgq]";
const SEP = "[ \\t\\-.]*";

/* เลขบัตรบนบัตรจริงพิมพ์เป็นกลุ่ม "1 2345 67890 12 3" — ยอมให้มีช่องว่าง/ขีดคั่น
   แต่ไม่ข้ามบรรทัด กันไปจับเลขคนละชุดมาต่อกัน
   ทุกผลลัพธ์ต้องผ่าน checksum เสมอ — ยอมปล่อยว่างดีกว่ากรอกเลขผิด */
function extractThaiId(text) {
  // รอบแรก — ตัวเลขล้วน
  const strict = new RegExp(`\\d(?:${SEP}\\d){12}`, "g");
  let m;
  while ((m = strict.exec(text))) {
    const digits = m[0].replace(/\D/g, "");
    if (validateThaiId(digits)) return digits;
  }

  // รอบสอง — เผื่อ OCR อ่านตัวเลขบางหลักเป็นตัวอักษรหน้าตาคล้ายกัน (0↔O, 1↔l, 5↔S)
  // OCR มักอ่านเพี้ยนแบบเดียวกันทั้งใบ (เลข 0 ทุกตัวกลายเป็น O) จึงต้องเผื่อไว้เยอะ
  // แต่ยังบังคับให้มีตัวเลขจริงอย่างน้อย 6 หลัก — ข้อความภาษาอังกฤษไม่มีทางเข้าเกณฑ์นี้
  const loose = new RegExp(`${DIGITISH}(?:${SEP}${DIGITISH}){12}`, "g");
  while ((m = loose.exec(text))) {
    const raw = m[0].replace(/[ \t\-.]/g, "");
    if ((raw.match(/\d/g) || []).length < 6) continue;
    const digits = raw.replace(/[^0-9]/g, (c) => OCR_DIGIT_FIX[c] || "");
    if (digits.length === 13 && validateThaiId(digits)) return digits;
  }
  return "";
}

/* ═══════════════ helper: วันที่ ═══════════════ */
const MONTHS = {
  มค: 1, กพ: 2, มีค: 3, เมย: 4, พค: 5, มิย: 6,
  กค: 7, สค: 8, กย: 9, ตค: 10, พย: 11, ธค: 12,
  มกราคม: 1, กุมภาพันธ์: 2, มีนาคม: 3, เมษายน: 4, พฤษภาคม: 5, มิถุนายน: 6,
  กรกฎาคม: 7, สิงหาคม: 8, กันยายน: 9, ตุลาคม: 10, พฤศจิกายน: 11, ธันวาคม: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
  january: 1, february: 2, march: 3, april: 4, june: 6, july: 7,
  august: 8, september: 9, october: 10, november: 11, december: 12,
};

const monthOf = (token) => MONTHS[String(token || "").replace(/\./g, "").toLowerCase()] || 0;

/** ปี พ.ศ. → ค.ศ. (บัตรไทยพิมพ์ พ.ศ., พาสปอร์ตพิมพ์ ค.ศ.) */
const toGregorianYear = (year) => (year > 2200 ? year - 543 : year);

function toISO(year, month, day) {
  const y = toGregorianYear(year);
  if (!y || !month || !day) return "";
  if (y < 1900 || y > new Date().getFullYear()) return "";
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** "1 ม.ค. 2530" / "1 Jan. 1987" / "01/01/2530" → "1987-01-01" (คืน "" ถ้าอ่านไม่ออก) */
export function thaiDateToISO(input) {
  const s = normalizeDigits(input).trim();
  if (!s) return "";

  // รูปแบบตัวเลขล้วน 01/01/2530 หรือ 01-01-2530
  const numeric = s.match(/(\d{1,2})\s*[/\-.]\s*(\d{1,2})\s*[/\-.]\s*(\d{4})/);
  if (numeric) return toISO(Number(numeric[3]), Number(numeric[2]), Number(numeric[1]));

  // รูปแบบมีชื่อเดือน — ทั้งไทยและอังกฤษ
  const named = s.match(/(\d{1,2})\s*([ก-๙.]{2,12}|[A-Za-z]{3,12}\.?)\s*(\d{4})/);
  if (named) return toISO(Number(named[3]), monthOf(named[2]), Number(named[1]));

  return "";
}

/* ═══════════════ helper: ชื่อ / เพศ ═══════════════ */
/* เรียงคำนำหน้าจากยาวไปสั้น กัน "นางสาว" ถูกจับเป็น "นาง" ก่อน */
const TITLE_RE = /(นางสาว|น\.ส\.|เด็กหญิง|ด\.ญ\.|เด็กชาย|ด\.ช\.|นาย|นาง)\s*([ก-๙]+(?:\s+[ก-๙]+){0,3})/;

const cleanName = (s) =>
  String(s || "").replace(/[^ก-๙\s.]/g, " ").replace(/\s+/g, " ").trim();

function extractThaiName(lines) {
  // 1) จาก label บนบัตร
  for (const line of lines) {
    const m = line.match(/ชื่อ\s*ตัว\s*และ\s*ชื่อ\s*สกุล\s*(.+)/);
    if (m) {
      const value = cleanName(m[1]);
      if (value.length >= 3) return value;
    }
  }
  // 2) สำรอง — หาบรรทัดที่ขึ้นต้นด้วยคำนำหน้าชื่อไทย (เผื่อ label อ่านไม่ออก)
  for (const line of lines) {
    const m = line.match(TITLE_RE);
    if (m) {
      const value = cleanName(m[0]);
      if (value.length >= 3) return value;
    }
  }
  return "";
}

/** บัตรประชาชนไทยไม่พิมพ์เพศตรงๆ — เดาจากคำนำหน้าชื่อ
    ค่าที่คืนต้องตรงกับ <option value> ในฟอร์มเช็คอินเป๊ะๆ */
export function genderFromTitle(name) {
  const s = String(name || "").trim();
  if (/^(นางสาว|น\.ส\.|นาง|เด็กหญิง|ด\.ญ\.)/.test(s)) return "หญิง";
  if (/^(นาย|เด็กชาย|ด\.ช\.)/.test(s))                return "ชาย";
  if (/^(mrs|miss|ms)\b/i.test(s))                     return "หญิง";
  if (/^mr\b/i.test(s))                                return "ชาย";
  return "";
}

/* ═══════════════ helper: ที่อยู่ ═══════════════ */
const ADDRESS_STOP = /วันออกบัตร|วันบัตรหมดอายุ|Date of Issue|Date of Expiry|เลขที่หลังบัตร|Laser/i;
const BIRTH_LINE   = /เกิดวันที่|วันเกิด|Date of Birth/i;
/* คำที่บอกว่าบรรทัดนี้เป็นที่อยู่ — ใช้ตอน label "ที่อยู่" อ่านไม่ออก */
const ADDRESS_HINT = /หมู่|ต\.|อ\.|จ\.|ถ\.|ซ\.|แขวง|เขต|ตำบล|อำเภอ|จังหวัด|บ้านเลขที่/;

/* ตัดขยะที่ไม่ใช่ไทย/ตัวเลขที่ค้างอยู่หน้าบรรทัด (เศษของ label ที่ OCR อ่านเพี้ยน) */
const stripJunkPrefix = (s) => s.replace(/^[^ก-๙0-9]+/, "").trim();

/* คำไทยที่ขึ้นต้นที่อยู่ได้จริง — ห้ามตัดทิ้ง */
const ADDRESS_KEEP = /^(หมู่บ้าน|บ้านเลขที่|เลขที่|หมู่|ซอย|ถนน|ตำบล|อำเภอ|จังหวัด|แขวง|เขต|บ้าน)/;

/* บนบัตรจริง label "ที่อยู่" ตัวเล็กมากและมัก OCR เพี้ยนเป็นคำไทยมั่วๆ ("หือยู่", "ฟีอย")
   ที่อยู่บนบัตรขึ้นต้นด้วยบ้านเลขที่เสมอ — คำไทยสั้นๆ ที่ค้างอยู่หน้าตัวเลขจึงคือเศษ label */
const stripGarbledLabel = (s) =>
  ADDRESS_KEEP.test(s) ? s : s.replace(/^[ก-๙]{2,8}\s+(?=\d)/, "");

function joinAddress(parts) {
  return parts.slice(0, 4).join(" ").replace(/\s+/g, " ").trim();
}

function extractAddress(lines) {
  // 1) จาก label ตรงๆ
  const start = lines.findIndex((l) => l.includes("ที่อยู่"));
  if (start >= 0) {
    const parts = [];
    const head = stripJunkPrefix(lines[start].replace(/^.*ที่อยู่\s*/, ""));
    if (head) parts.push(head);
    for (let i = start + 1; i < lines.length && parts.length < 4; i++) {
      if (ADDRESS_STOP.test(lines[i])) break;
      if (lines[i]) parts.push(lines[i]);
    }
    if (parts.length) return joinAddress(parts);
  }

  // 2) สำรอง — บนบัตรจริง label "ที่อยู่" ตัวเล็กมากและมัก OCR เพี้ยน
  //    ที่อยู่อยู่ระหว่างบรรทัดวันเกิดกับบรรทัดวันออกบัตรเสมอ จึงจำกัดขอบเขตด้วยสองจุดนี้
  let from = 0;
  for (let i = 0; i < lines.length; i++) if (BIRTH_LINE.test(lines[i])) from = i + 1;
  let to = lines.findIndex((l, i) => i >= from && ADDRESS_STOP.test(l));
  if (to < 0) to = lines.length;

  const parts = [];
  for (let i = from; i < to && parts.length < 4; i++) {
    if (ADDRESS_HINT.test(lines[i])) {
      const cleaned = stripJunkPrefix(lines[i]);
      parts.push(parts.length === 0 ? stripGarbledLabel(cleaned) : cleaned);
    } else if (parts.length) break;   // ที่อยู่ต้องเป็นบรรทัดติดกัน
  }
  return joinAddress(parts);
}

/* ═══════════════ พาสปอร์ต: MRZ (TD3 — 2 บรรทัด บรรทัดละ 44 ตัว) ═══════════════ */
const COUNTRY_BY_ISO3 = {
  THA: "ไทย", USA: "สหรัฐอเมริกา", GBR: "สหราชอาณาจักร", CHN: "จีน", JPN: "ญี่ปุ่น",
  KOR: "เกาหลีใต้", TWN: "ไต้หวัน", HKG: "ฮ่องกง", SGP: "สิงคโปร์", MYS: "มาเลเซีย",
  IDN: "อินโดนีเซีย", VNM: "เวียดนาม", PHL: "ฟิลิปปินส์", LAO: "ลาว", KHM: "กัมพูชา",
  MMR: "เมียนมา", IND: "อินเดีย", AUS: "ออสเตรเลีย", NZL: "นิวซีแลนด์", CAN: "แคนาดา",
  DEU: "เยอรมนี", FRA: "ฝรั่งเศส", ITA: "อิตาลี", ESP: "สเปน", NLD: "เนเธอร์แลนด์",
  RUS: "รัสเซีย", SWE: "สวีเดน", NOR: "นอร์เวย์", DNK: "เดนมาร์ก", FIN: "ฟินแลนด์",
  CHE: "สวิตเซอร์แลนด์", AUT: "ออสเตรีย", BEL: "เบลเยียม", POL: "โปแลนด์",
  ISR: "อิสราเอล", ARE: "สหรัฐอาหรับเอมิเรตส์", SAU: "ซาอุดีอาระเบีย", ZAF: "แอฟริกาใต้",
  BRA: "บราซิล", MEX: "เม็กซิโก", ARG: "อาร์เจนตินา",
};

function findMrzLines(lines) {
  const norm = lines.map((l) => l.replace(/\s+/g, "").toUpperCase().replace(/[«»‹›]/g, "<"));
  for (let i = 0; i < norm.length - 1; i++) {
    const a = norm[i], b = norm[i + 1];
    if (/^P[<A-Z0-9]{20,}$/.test(a) && a.includes("<<") && /^[A-Z0-9<]{20,}$/.test(b)) {
      return [a.padEnd(44, "<").slice(0, 44), b.padEnd(44, "<").slice(0, 44)];
    }
  }
  return null;
}

/** YYMMDD ของวันเกิดใน MRZ — ปี 2 หลัก ต้องเดาศตวรรษ (วันเกิดต้องไม่อยู่ในอนาคต) */
function mrzBirthToISO(yymmdd) {
  if (!/^\d{6}$/.test(yymmdd)) return "";
  const yy = Number(yymmdd.slice(0, 2));
  const currentYY = new Date().getFullYear() % 100;
  const year = yy > currentYY ? 1900 + yy : 2000 + yy;
  return toISO(year, Number(yymmdd.slice(2, 4)), Number(yymmdd.slice(4, 6)));
}

export function parseMrz(lines) {
  const mrz = findMrzLines(lines);
  if (!mrz) return null;
  const [l1, l2] = mrz;

  const [surnameRaw = "", givenRaw = ""] = l1.slice(5).split("<<");
  const surname = surnameRaw.replace(/</g, " ").replace(/\s+/g, " ").trim();
  const given   = givenRaw.replace(/</g, " ").replace(/\s+/g, " ").trim();
  const fullName = [given, surname].filter(Boolean).join(" ");

  const passportNo = l2.slice(0, 9).replace(/</g, "").trim();
  const iso3       = l2.slice(10, 13).replace(/</g, "").trim();
  const birthDate  = mrzBirthToISO(l2.slice(13, 19));
  const sex        = l2.slice(20, 21);

  const country = COUNTRY_BY_ISO3[iso3] || iso3;

  return {
    ...EMPTY_FIELDS,
    docType:   "passport",
    idCard:    /^[A-Z0-9]{6,9}$/.test(passportNo) ? passportNo : "",
    fullName,
    birthDate,
    gender:    sex === "M" ? "ชาย" : sex === "F" ? "หญิง" : "",
    // ที่อยู่ไม่มีใน MRZ — ปล่อยว่างให้แอดมินกรอกเอง
    nationality: country,
    country,
  };
}

/* ═══════════════ parser หลัก ═══════════════ */
/**
 * แปลงข้อความดิบจาก OCR เป็นค่าที่กรอกลงฟอร์มเช็คอินได้
 * ตรวจ MRZ ของพาสปอร์ตก่อน ถ้าไม่เจอจึงถือเป็นบัตรประชาชนไทย
 */
export function parseIdCardText(rawText) {
  const text  = normalizeThaiText(normalizeDigits(rawText || ""));
  const lines = text.split(/\r?\n/).map((l) => l.replace(/\s+/g, " ").trim()).filter(Boolean);
  if (!lines.length) return { ...EMPTY_FIELDS };

  const passport = parseMrz(lines);
  if (passport) return passport;

  const idCard   = extractThaiId(text);
  const fullName = extractThaiName(lines);
  const address  = extractAddress(lines);

  // บัตรมี 3 วันที่ (เกิด / ออกบัตร / หมดอายุ) — ต้องยึดจาก label เท่านั้น ห้ามเดา
  const birthLine = lines.find((l) => /เกิดวันที่|วันเกิด|Date of Birth/i.test(l)) || "";
  const birthDate = thaiDateToISO(birthLine.replace(/.*?(เกิดวันที่|วันเกิด|Date of Birth)/i, ""));

  // ถือว่าเป็นบัตรไทยเมื่อเลขบัตรผ่าน checksum หรือเจอคำบนหน้าบัตรชัดเจน
  const isThaiCard = Boolean(idCard) || /บัตรประจำตัวประชาชน|เลขประจำตัวประชาชน/.test(text);

  return {
    ...EMPTY_FIELDS,
    docType: isThaiCard ? "thai_id" : "",
    idCard,
    fullName,
    birthDate,
    gender: genderFromTitle(fullName),
    address,
    nationality: isThaiCard ? "ไทย" : "",
    country:     isThaiCard ? "ไทย" : "",
  };
}
